import { AvatarNewTag } from '@/components/Cards/AvatarNewTag';
import { AvatarTag } from '@/components/Cards/AvatarTag';
import { AvatarTypeTag } from '@/components/Cards/AvatarTypeTag';
import { shouldShowRawMenuItem } from '@/components/Dropdown/MenuItems/RawDataMenuItem';
import { LibraryItemHoverButton } from '@/components/Library/LibraryItemHoverButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { BackgroundOverlay } from '@/components/Tile';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { expressFormulaIntercept, expressFormulaSlope, expressWorkerActivationTime } from '@/environment';
import DangerCircle2 from '@/images/DangerCircle2.svg';
import FlashLightning from '@/images/FlashLightning.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import PenOutlined from '@/images/PenOutlined.svg';
import SentimentTagEmoji from '@/images/sentiment/SentimentTagEmoji.svg';
import { isNewPresenter } from '@/pages/agents/components/avatars/utils/IsNewPresenter';
import { isGreenScreenAvatar, isUserAvatar, isUserClipAvatar } from '@/pages/avatar';
import { getErrorTextByKind } from '@/pages/avatar/common/getErrorText';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { AvatarStatus, isDvpPresenter, isDvpSelfServePresenter, isDvpTrain, isExpressPresenter, Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Tooltip } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AvatarFavouriteButton, AvatarOptionsList, AvatarPreviewModal, InActiveBanner } from './components';
import { AvatarProgressBar } from './components/AvatarProgressBar';
import { VoiceCloneErrorBanner } from './components/VoiceCloneError';

const ControlsContainer = styled.div`
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 5;
    transition: all 0.2s ease-in-out;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const ImageContainer = styled.div<{ hovered?: boolean; disabled?: boolean }>`
    border-radius: 12px;
    border: ${({ theme }) => `1px solid ${theme.colors.black[300]}`};
    ${({ disabled }) => !disabled && `cursor: pointer;`}
    width: 100%;
    aspect-ratio: 16/10;
    position: relative;
    overflow: hidden;

    ${({ hovered }) =>
        hovered &&
        `
        .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }
    
        ${ControlsContainer} {
            opacity: 1;
        }
    `}

    :hover .BackgroundOverlay {
        opacity: 0.5;
        transform: scale(105%);
    }

    :hover ${ControlsContainer} {
        opacity: 1;
    }
`;

const Title = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    margin-top: 12px;
    text-transform: capitalize;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const UpgradeText = styled.span`
    margin-block-start: 8px;
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 16.09px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.322px;
`;

const StyledAvatarNewTag = styled(AvatarNewTag)`
    top: 16px;
    right: 16px;
    position: absolute;
    z-index: 1;
`;

const SentimentsTag = styled.div`
    display: inline-flex;
    height: 20px;
    padding: 0px 4px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    border-radius: 4px;
    background: rgba(9, 6, 4, 0.85);

    backdrop-filter: blur(8px);

    color: ${theme.colors.white};
    font-family: Manrope;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 10.118px;
`;

const DeprecationTag = styled(AvatarTag)`
    background-color: ${theme.colors.black[100]};
    span {
        color: ${theme.colors.secondary.main};
    }
`;

const Footer = styled.div`
    position: absolute;
    bottom: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 6px;
`;

const TagsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
    padding: 8px 16px;
`;
const StyledAvatarFavouriteButton = styled(AvatarFavouriteButton)`
    top: 16px;
    left: 16px;
    position: absolute;
    z-index: 15;
`;

const ErrorImage = styled.img`
    width: 40px;
    height: 40px;
`;

const MiddleContainer = styled.div`
    width: 100%;
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    color: ${({ theme }) => theme.colors.secondary[900]};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.32px;
    height: 50%;
    top: 50%;
    transform: translate(0%, -50%);
    z-index: 5;
`;

const AvatarErrorTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const StyledDraftTag = styled(AvatarTag)`
    background-color: ${({ theme }) => theme.colors.black[100]};

    .draft-tag {
        color: ${({ theme }) => theme.colors.secondary.main};
    }
`;

const UploadingSpinner = styled(Player)`
    height: 78px;
`;

const UploadingText = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
`;

/**
 * Calculate the processing time based on the duration for the express worker.
 * @param duration The duration of the video in seconds.
 * @returns The processing time in seconds rounded to one decimal place. Formula: processingTime = slope * duration + intercept
 * @example processingTime = 13.9303 * duration + 13.8923
 */
export const calculateProcessingTime = (duration: number) =>
    Math.round((expressFormulaSlope * duration + expressFormulaIntercept) * 10) / 10 + expressWorkerActivationTime;

interface Props {
    avatar: Presenter;
    editable?: boolean;
    isAiGenerated?: boolean;
}

const isUserPresenter = (avatar: Presenter) => isExpressPresenter(avatar) || isDvpPresenter(avatar) || isDvpTrain(avatar);

export const AvatarTile = ({ avatar, editable, isAiGenerated }: Props) => {
    const [keepHover, setKeepHover] = useState(false);
    const [isPreviewViewed, setIsPreviewViewed] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const showBlur = (isUserAvatar(avatar) || isUserClipAvatar(avatar)) && !isDvpPresenter(avatar) && !isGreenScreenAvatar(avatar);
    const isLoading =
        (isUserPresenter(avatar) &&
            [AvatarStatus.Started, AvatarStatus.Created, AvatarStatus.TrainingStarted, AvatarStatus.Validating].includes(avatar.status)) ||
        (isDvpTrain(avatar) && avatar.status === AvatarStatus.Done);
    // If the avatar is not an express presenter, it is considered done. Otherwise, check the status.
    const isDone = isExpressPresenter(avatar)
        ? avatar.status === AvatarStatus.Done
        : isDvpSelfServePresenter(avatar) || !isDvpTrain(avatar);
    const isDraft = isExpressPresenter(avatar) && avatar.status === AvatarStatus.Draft; // TODO: Change this to user presenter after draft is implemented
    const isVoiceError = isDvpSelfServePresenter(avatar) && !avatar.voice;
    const isNew = useMemo(() => isNewPresenter(avatar), [avatar]);
    const isInactive = false;
    const isError = [AvatarStatus.Error, AvatarStatus.Rejected].includes(avatar.status);
    const isUploading = avatar.status === AvatarStatus.Uploading;
    const showContextMenu = shouldShowRawMenuItem(user) || (editable && !isUploading && !isLoading);
    const isDisabled =
        isInactive ||
        [
            AvatarStatus.Started,
            AvatarStatus.TrainingStarted,
            AvatarStatus.Validating,
            isDvpTrain(avatar) ? AvatarStatus.Done : AvatarStatus.Error,
        ].includes(avatar.status);

    return (
        <Container className={isPreviewViewed && !isPreviewModalOpen ? 'premium_plus_viewed' : ''}>
            <ImageContainer hovered={keepHover} disabled={isDisabled}>
                <BackgroundOverlay
                    bg={avatar.thumbnail || avatar.poster}
                    isLoading={!isDone}
                    showBlur={showBlur}
                    bgColor="white"
                    disabled={isDisabled}
                />

                <ControlsContainer>
                    {(isDone || isDraft) && (
                        <StyledAvatarFavouriteButton avatarId={avatar.id} testId={`avatar-tile-favorite-button-${avatar.id}`} />
                    )}

                    {isDone && (
                        <AvatarPreviewModal
                            avatar={avatar}
                            editable={editable}
                            onPreviewViewed={() => setIsPreviewViewed(true)}
                            onModalStateChange={setIsPreviewModalOpen}
                        />
                    )}
                    {isDraft && (
                        <LibraryItemHoverButton
                            onClick={() => navigate(AppRoutes.AvatarEditPage, { state: avatar })}
                            icon={<MaskedIcon src={PenOutlined} size="16px" />}
                            text="Edit draft"
                            color="outlined"
                        />
                    )}
                    {showContextMenu && (
                        <AvatarOptionsList
                            editable={editable ?? false}
                            avatar={avatar}
                            onOpen={() => setKeepHover(true)}
                            onClose={() => setKeepHover(false)}
                        />
                    )}
                </ControlsContainer>

                {isInactive && (
                    <MiddleContainer>
                        <MaskedIcon src={FlashLightning} size={'24px'} />
                        <UpgradeText>Upgrade plan to use</UpgradeText>
                    </MiddleContainer>
                )}

                {isUploading && (
                    <MiddleContainer>
                        <UploadingSpinner src={LoadingAnimation} autoplay loop />
                        <UploadingText>Uploading...</UploadingText>
                    </MiddleContainer>
                )}

                {isLoading && (
                    <MiddleContainer>
                        <AvatarProgressBar avatar={avatar} />
                    </MiddleContainer>
                )}

                {isError && (
                    <MiddleContainer>
                        <CustomTooltip cursor="pointer" placement="top" title={getErrorTextByKind(avatar.error)} whitespace="normal">
                            <AvatarErrorTooltipContent>
                                <ErrorImage src={DangerCircle2} />
                                Failed to create Avatar
                            </AvatarErrorTooltipContent>
                        </CustomTooltip>
                    </MiddleContainer>
                )}

                <StyledAvatarNewTag isNew={isNew} />
                <Footer>
                    <TagsContainer>
                        <AvatarTypeTag avatarId={avatar.id} type={avatar.type} isAiGenerated={isAiGenerated} />
                        {isDraft && <StyledDraftTag text="Draft" tagClassName="draft-tag" type="draft" />}
                        {!!avatar.sentiments?.length && avatar.sentiments?.length > 1 && (
                            <SentimentsTag>
                                <img src={SentimentTagEmoji} alt="Sentiment Tag Emoji" />
                                {avatar.sentiments?.length}
                            </SentimentsTag>
                        )}
                        {avatar.deprecationDate && (
                            <Tooltip
                                title={`This Avatar will be discontinued on ${format(parseISO(avatar.deprecationDate), 'MMMM do')}`}
                                slotProps={{
                                    tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } },
                                }}
                                PopperProps={{
                                    modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
                                }}
                                placement="top">
                                <div>
                                    <DeprecationTag text="Deprecating" type="deprecating" />
                                </div>
                            </Tooltip>
                        )}
                    </TagsContainer>
                    {isInactive && <InActiveBanner />}
                    {!isInactive && isVoiceError && <VoiceCloneErrorBanner />}
                </Footer>
            </ImageContainer>

            <Title>{avatar.name}</Title>
        </Container>
    );
};
